import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { LogoFull } from "./Logo";
import { scrollToSection } from "../lib/scroll";

const LINKS = [
    { id: "christ", label: "Christ Wash" },
    { id: "autoriparatore", label: "Autoriparatore" },
    { id: "servizi", label: "Servizi" },
    { id: "chi-siamo", label: "Chi Siamo" },
    { id: "realizzazioni", label: "Realizzazioni" },
    { id: "assistenza", label: "Assistenza" },
    { id: "contatti", label: "Contatti" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [progress, setProgress] = useState(0);
    const [active, setActive] = useState("");

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            setScrolled(y > 24);
            const max = document.documentElement.scrollHeight - window.innerHeight;
            setProgress(max > 0 ? Math.min(100, (y / max) * 100) : 0);
            let current = "";
            for (const l of LINKS) {
                const el = document.getElementById(l.id);
                if (el && el.getBoundingClientRect().top <= 140) current = l.id;
            }
            setActive(current);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToSection(id);
    };

    return (
        <header
            data-testid="nav-bar"
            className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
                scrolled
                    ? "border-slate-200 bg-white/90 shadow-[0_4px_24px_-8px_rgba(11,31,58,0.12)] backdrop-blur-xl"
                    : "border-transparent bg-white/70 backdrop-blur-md"
            }`}
        >
            <div
                aria-hidden="true"
                data-testid="scroll-progress"
                className="absolute inset-x-0 top-0 h-[3px] origin-left bg-[#E30613]"
                style={{ transform: `scaleX(${progress / 100})` }}
            />
            <div className="mx-auto flex h-[76px] max-w-[1440px] items-center justify-between px-4 sm:px-8 lg:px-16">
                <button
                    onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
                    className="flex items-center gap-4"
                    data-testid="nav-logo-button"
                >
                    <LogoFull />
                </button>

                <nav className="hidden items-center gap-5 lg:flex xl:gap-7" data-testid="nav-menu">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            aria-current={active === l.id ? "true" : undefined}
                            className={`group relative whitespace-nowrap text-sm font-semibold transition-colors hover:text-[#0B1F3A] ${
                                active === l.id ? "text-[#0B1F3A]" : "text-[#0B1F3A]/70"
                            }`}
                        >
                            {l.label}
                            <span
                                className={`absolute -bottom-1.5 left-0 h-[2px] bg-[#E30613] transition-[width] duration-300 group-hover:w-full ${
                                    active === l.id ? "w-full" : "w-0"
                                }`}
                            />
                        </button>
                    ))}
                    <button
                        data-testid="nav-cta-button"
                        onClick={() => go("contatti")}
                        className="group flex items-center gap-2 whitespace-nowrap rounded-full bg-[#E30613] px-5 py-2.5 text-sm font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[#C20410] hover:shadow-[0_8px_24px_-6px_rgba(227,6,19,0.5)] active:scale-95"
                    >
                        Richiedi Preventivo
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </button>
                </nav>

                <button
                    data-testid="mobile-menu-button"
                    onClick={() => setOpen(!open)}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-[#0B1F3A] lg:hidden"
                    aria-label="Menu"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-nav-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden border-t border-slate-100 bg-white lg:hidden"
                    >
                        <div className="flex flex-col gap-1 px-4 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="rounded-lg px-4 py-3 text-left text-base font-semibold text-[#0B1F3A] transition-colors hover:bg-slate-50"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="mobile-nav-cta-button"
                                onClick={() => go("contatti")}
                                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#E30613] px-5 py-3.5 text-sm font-bold text-white"
                            >
                                Richiedi Preventivo
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};
